// api/route_completions.js

import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';

/**
 * Bulk-check which route_uuids have a completion entry.
 * @param {string[]} routeUuids
 * @returns {{ completed: { [uuid: string]: boolean } } | null}
 */
export const checkCompletions = async (routeUuids) => {
    if (!routeUuids || routeUuids.length === 0) return { completed: {} };
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch('/api/route-completions/completions/check', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ route_uuids: routeUuids }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error checking route completions:', error);
        return { completed: {} };
    }
};

/**
 * Fetch the full completion record for a single route by its UUID.
 * @param {string} routeUuid
 * @returns {{ success: boolean, completion: object | null }}
 */
export const getCompletionByUuid = async (routeUuid) => {

    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/api/route-completions/completions/by-uuid/${encodeURIComponent(routeUuid)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            const errorMessage = err.error || `HTTP ${response.status}`;
            logger.error('API Error fetching route completion:', errorMessage);
            return { success: false, completion: null, error: errorMessage };
        }
        return await response.json();
    } catch (error) {
        logger.error('Error fetching route completion:', error);
        return { success: false, completion: null, error: error.message || 'Unknown error occurred' };
    }
};

/**
 * Fetch the full completion record for a single route by its route_ref (consignment number).
 * @param {string} routeRef
 * @returns {{ success: boolean, completion: object | null }}
 */
/**
 * Get the public URL for an image stored in the route-completions bucket.
 * @param {string} imagePath  bare filename or full storage path
 * @returns {{ success: boolean, image_url: string | null }}
 */
export const getCompletionImageUrl = async (imagePath) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(
            `/api/route-completions/image-url?path=${encodeURIComponent(imagePath)}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`,
                },
            }
        );
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error getting completion image URL:', error);
        return { success: false, image_url: null };
    }
};

export const getCompletionByRef = async (routeRef) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/api/route-completions/completions/by-ref/${encodeURIComponent(routeRef)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error fetching route completion by ref:', error);
        return { success: false, completion: null };
    }
};

/**
 * Generate a delivery note PDF for a completed route and trigger download.
 * @param {object} route  – full route object from calendar state
 * @param {object} completion – completion record (with signed_by, customer_signature_data, delivery_images, paperwork_images)
 * @returns {{ message: string, status: number }}
 */
export const generateDeliveryNote = async (route, completion) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch('/routing/generate-delivery-note-with-completion', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ route, completion }),
        });

        if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.error || `HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `DeliveryNote-${Date.now()}.pdf`;
        if (contentDisposition) {
            const match = contentDisposition.match(/filename\*?=["']?([^;"']+)\*?/);
            if (match) filename = decodeURIComponent(match[1]).replace(/["']/g, '');
        }

        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { message: `Downloaded: ${filename}`, status: 200 };
    } catch (error) {
        logger.error('Error generating delivery note:', error);
        return { message: error.message || 'Failed to generate delivery note', status: 500 };
    }
};

/**
 * Upload an image file to the route-completions storage bucket via the backend.
 * @param {File} file - The File object to upload
 * @returns {{ success: boolean, path: string | null }}
 */
export const uploadCompletionImage = async (file) => {
    try {
        const sessionToken = await getSessionUUID();
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/route-completions/upload-image', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: formData,
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error uploading completion image:', error);
        return { success: false, path: null };
    }
};

/**
 * Submit a manual POD (Pod Off) from the web interface.
 * @param {object} data - The completion data
 * @returns {{ success: boolean, completion: object | null }}
 */
export const submitManualCompletion = async (data) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch('/api/route-completions/completions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        logger.error('Error submitting manual completion:', error);
        return { success: false, completion: null, error: error.message };
    }
};
